import { SearchIcon } from 'lucide-react'
import Link from 'next/link'
import { cn } from '~/src/lib/utils'
import SaleTagsIcon from '~/src/components/svg/SaleTagsIcon'
import AppIcon from '~/src/components/svg/AppIcon'
import TradeIcon from '~/src/components/svg/TradeIcon'
import BlogIcon from '~/src/components/svg/BlogIcon'
import { Badge } from '~/src/components/ui/badge'
import ProductsIcon from '~/src/components/svg/ProductsIcon'
import RewardsIcon from '~/src/components/svg/RewardsIcon'
import useGetUserActivityCounts from '../hooks/user/useGetUserActivityCounts'

type NavItem = {
  href: string
  label: React.ReactNode
  icon?: React.ReactNode
  badge?: React.ReactNode
  target?: string
  className?: string
}

const navItems: NavItem[] = [
  { href: '/', label: 'New Search', icon: <SearchIcon strokeWidth={2.5} /> },
  { href: '/products/furniture', label: 'Products', icon: <ProductsIcon /> },
  {
    href: '/clearance',
    label: 'Sales',
    icon: <SaleTagsIcon />,
  },
]

const secondaryNavItems: NavItem[] = [
  { href: '/app', label: 'App', icon: <AppIcon /> },
  {
    href: '/trade',
    label: 'Trade',
    target: '_blank',
    icon: <TradeIcon />,
    badge: <Badge className='!bg-orange-400 text-xs text-white'>Pro</Badge>,
  },
  { href: '/blog', label: 'Blog', target: '_blank', icon: <BlogIcon /> },
]

interface Props {
  showIndicator?: boolean
  hideHome?: boolean
  hideSecondaryNav?: boolean
  isSheetOpen?: boolean
  hasCustomBackground?: boolean
  isHome?: boolean
}

const HeaderNavigation: React.FC<Props> = ({
  hideHome,
  hideSecondaryNav,
  isSheetOpen,
  hasCustomBackground = false,
  isHome = false,
}) => {
  const { data: activityCounts } = useGetUserActivityCounts({
    enabled: isSheetOpen,
  })

  const totalUnclaimedPoints = activityCounts?.totalUnclaimedPoints ?? 0
  const totalClaimedPoints = activityCounts?.totalClaimedPoints ?? 0
  const totalPoints = totalClaimedPoints || totalUnclaimedPoints

  const filteredNavItems = navItems.filter(
    (item) => !hideHome || item.href !== '/'
  )

  return (
    <div className='flex flex-col gap-4 px-2'>
      <nav className={cn('flex flex-col')}>
        {filteredNavItems.map((item, index) => (
          <NavItem
            key={index}
            {...item}
            hasCustomBackground={hasCustomBackground}
            isHome={isHome}
          />
        ))}
      </nav>
      <nav className={cn('flex flex-col')}>
        {!hideSecondaryNav &&
          secondaryNavItems.map((item, index) => (
            <NavItem
              key={index}
              {...item}
              hasCustomBackground={hasCustomBackground}
              isHome={isHome}
            />
          ))}
      </nav>
      <div className=''>
        <NavItem
          href='/$dupe/rewards'
          label={
            <span className='flex items-center gap-1.5'>
              <span>Rewards</span>
              <span className='bg-dupelight rounded-md px-1.5 py-0.5 text-xs font-semibold text-black'>
                {totalPoints} pts
              </span>
            </span>
          }
          icon={<RewardsIcon />}
          hasCustomBackground={hasCustomBackground}
          isHome={isHome}
        />
      </div>
    </div>
  )
}

const NavItem = (
  item: NavItem & { hasCustomBackground?: boolean; isHome?: boolean }
) => {
  return (
    <Link
      href={item.href}
      className={cn(
        'block rounded-lg p-3 py-2 md:py-3',
        // Force light mode styling on home page or custom background pages
        item.hasCustomBackground
          ? 'bg-white text-black/70 transition-colors md:hover:bg-gray-100'
          : item.isHome // force dark mode on home page
            ? 'bg-darkest  text-white/70 transition-colors md:hover:bg-black/50'
            : 'dark:bg-darkest bg-white text-black/70 transition-colors md:hover:bg-gray-100 dark:text-white/70 dark:md:hover:bg-black/50',
        'flex items-center gap-2.5',
        'font-medium md:text-lg',
        item.className
      )}
      target={item.target}
    >
      {item.icon}
      <span className='inline-flex items-center gap-1.5'>
        <span>{item.label}</span>
        {item.badge}
      </span>
    </Link>
  )
}

export default HeaderNavigation
